import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Checkbox,
  Stack,
  Typography,
} from '@mui/material';
import JSZip from 'jszip';
import { useTranslation } from 'react-i18next';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';

const defaultReadme = (meta) => `Export generated by Ichneumonoidea-of-the-World

Date: ${new Date().toISOString()}
Source: ${meta.url}
Note: This archive contains parameters, chart exports and metadata. Please cite the database if used in publications.
`;

const OutputDialog = ({ open, onClose, settings, chartRef, chartData, fitResults }) => {
  const { t } = useTranslation(['analysis', 'common']);
  const [includeParams, setIncludeParams] = useState(true);
  const [format, setFormat] = useState('svg');
  const [folderName, setFolderName] = useState(() => `TSE_export_${new Date().toISOString().replace(/[:.]/g, '-')}`);
  const [loading, setLoading] = useState(false);

  const serializeParameters = () => {
    const lines = [];
    lines.push('Parameters export');
    lines.push(`Date: ${new Date().toISOString()}`);
    lines.push('');
    lines.push('Settings (JSON):');
    lines.push(JSON.stringify(settings, null, 2));
    lines.push('');
    lines.push('Notes: This file describes the filters and bootstrap settings used to produce the analysis. Use responsibly and cite data source as appropriate.');
    return lines.join('\n');
  };

  const getSVGString = () => {
    if (!chartRef || !chartRef.current) return null;
    const svg = chartRef.current.querySelector('svg');
    if (!svg) return null;
    // inline styles may be needed, but assume SVG exported by Recharts is standalone enough
    return svg.outerHTML;
  };

  const svgToPngBlob = (svgString, width = 1200, height = 600) => new Promise((resolve, reject) => {
    const img = new Image();
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      // white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        URL.revokeObjectURL(url);
        if (blob) resolve(blob); else reject(new Error('Canvas toBlob failed'));
      }, 'image/png');
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });

  const makePdfBlobFromPng = async (pngBlob) => {
    const dataUrl = await new Promise((res) => {
      const reader = new FileReader();
      reader.onload = () => res(reader.result);
      reader.readAsDataURL(pngBlob);
    });
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const imgProps = doc.getImageProperties(dataUrl);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const ratio = Math.min(pageWidth / imgProps.width, pageHeight / imgProps.height);
    const w = imgProps.width * ratio;
    const h = imgProps.height * ratio;
    const x = (pageWidth - w) / 2;
    const y = (pageHeight - h) / 2;
    doc.addImage(dataUrl, 'PNG', x, y, w, h);
    const pdfBlob = doc.output('blob');
    return pdfBlob;
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      const zip = new JSZip();
      const meta = { url: window.location.href };
      // parameters.txt
      if (includeParams) {
        zip.file('parameters.txt', serializeParameters());
      }
      // README
      zip.file('README.txt', defaultReadme(meta));

      const svgString = getSVGString();
      if (format === 'svg' && svgString) {
        zip.file('chart.svg', svgString);
      } else if ((format === 'png' || format === 'pdf') && svgString) {
        // estimate width/height from svg viewBox or bounding box
        let width = 1200;
        let height = 600;
        try {
          const parser = new DOMParser();
          const doc = parser.parseFromString(svgString, 'image/svg+xml');
          const svgEl = doc.documentElement;
          const vb = svgEl.getAttribute('viewBox');
          if (vb) {
            const parts = vb.split(/\s+|,/).map(Number);
            if (parts.length >= 4 && Number.isFinite(parts[2]) && Number.isFinite(parts[3])) {
              width = parts[2];
              height = parts[3];
            }
          } else {
            const wAttr = parseFloat(svgEl.getAttribute('width'));
            const hAttr = parseFloat(svgEl.getAttribute('height'));
            if (Number.isFinite(wAttr)) width = wAttr;
            if (Number.isFinite(hAttr)) height = hAttr;
          }
        } catch (e) {
          // fallback
        }

        if (format === 'png' || format === 'pdf') {
          const pngBlob = await svgToPngBlob(svgString, Math.round(width), Math.round(height));
          if (format === 'png') zip.file('chart.png', pngBlob);
          if (format === 'pdf') {
            const pdfBlob = await makePdfBlobFromPng(pngBlob);
            zip.file('chart.pdf', pdfBlob);
          }
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `${folderName}.zip`);
      onClose();
    } catch (err) {
      // eslint-disable-next-line no-alert
      window.alert('Export failed');
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('estimationPage.outputDialogTitle') || 'Output Result'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControlLabel
            control={<Checkbox checked={includeParams} onChange={(e) => setIncludeParams(e.target.checked)} />}
            label={t('estimationPage.outputIncludeParameters') || 'Include parameters (parameters.txt)'}
          />

          <FormControl component="fieldset">
            <FormLabel component="legend">{t('estimationPage.outputChartFormat') || 'Chart format'}</FormLabel>
            <RadioGroup value={format} onChange={(e) => setFormat(e.target.value)} row>
              <FormControlLabel value="svg" control={<Radio />} label="SVG" />
              <FormControlLabel value="png" control={<Radio />} label="PNG" />
              <FormControlLabel value="pdf" control={<Radio />} label="PDF" />
            </RadioGroup>
          </FormControl>

          <TextField label={t('estimationPage.outputFolderName') || 'Folder name'} value={folderName} onChange={(e) => setFolderName(e.target.value)} fullWidth />

          <Typography variant="caption" color="text.secondary">{t('estimationPage.outputNote') || 'The export will include parameters.txt and a chart in the selected format plus README.txt.'}</Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:cancel') || 'Cancel'}</Button>
        <Button variant="contained" disabled={loading} onClick={handleExport}>{t('common:save') || 'Export'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default OutputDialog;
